import { Fragment, useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, Clock, RotateCcw, BookmarkPlus, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { catColor } from "@/lib/lore";
import { PageHeader } from "@/components/CategoryBadge";
import { SaveAnswerDialog } from "@/components/SaveAnswerDialog";

const EXAMPLES = ["How old is Kael in year 430?", "Who was at the Siege of Highmoor?", "Where is everyone at the end of the Prime Timeline?", "What do we know about the Emberblade?"];
const CITE_RX = /\[((?:[EV]\d+)(?:\s*,\s*[EV]\d+)*)\]/g;

function Citation({ c, onOpen }) {
  const color = c.type === "entry" ? catColor(c.category) : "#8B5CF6";
  return (
    <button onClick={() => onOpen(c)} data-testid={`citation-${c.id}`}
      className="mx-0.5 inline-flex items-center gap-1 rounded-sm border px-1.5 py-0 align-baseline text-xs transition-colors hover:bg-[#1A202C]"
      style={{ color, borderColor: `${color}55` }}>
      {c.type === "event" && <Clock className="h-3 w-3" />}{c.type === "entry" ? c.name : c.title}
    </button>
  );
}

function AnswerText({ text, citations, onOpen }) {
  const parts = text.split(CITE_RX);
  return (
    <div className="whitespace-pre-wrap leading-relaxed text-[#E8DFD1]" data-testid="archive-answer-text">
      {parts.map((p, i) => i % 2 === 1
        ? <Fragment key={i}>{p.split(/\s*,\s*/).map((ref) => citations[ref.toUpperCase()] ? <Citation key={ref} c={citations[ref.toUpperCase()]} onOpen={onOpen} /> : null)}</Fragment>
        : <Fragment key={i}>{p.split(/\*\*(.+?)\*\*/g).map((s, j) => (j % 2 ? <strong key={j} className="text-[#F3EBDD] font-semibold">{s}</strong> : s))}</Fragment>)}
    </div>
  );
}

function Exchange({ x, onOpen, i, canEdit, onSave }) {
  const sources = Object.values(x.citations || {});
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid={`archive-exchange-${i}`}>
      <div className="font-display text-2xl text-[#F3EBDD]" data-testid={`archive-question-${i}`}>{x.question}</div>
      {x.answer == null ? (
        <p className="font-display text-xl text-[#A8A090] lf-caret" data-testid="archive-thinking">The archivist is consulting the records</p>
      ) : (
        <div className="rounded-md border border-[#2D3442] bg-[#13171F] p-5 border-l-2 border-l-[#D97706]">
          <AnswerText text={x.answer} citations={x.citations} onOpen={onOpen} />
          <div className="mt-4 pt-3 border-t border-[#2D3442] flex items-center justify-between gap-3">
            <span className="lf-label" data-testid={`archive-sources-${i}`}>{sources.length ? `${sources.length} source${sources.length > 1 ? "s" : ""} cited` : "No sources cited"}</span>
            {canEdit && (
              x.savedId
                ? <button onClick={() => onOpen({ type: "entry", id: x.savedId })} className="flex items-center gap-1.5 text-xs text-[#10B981] hover:underline" data-testid={`archive-saved-${i}`}><Check className="h-3.5 w-3.5" />Saved to lore · open</button>
                : <Button size="sm" variant="ghost" className="text-[#F59E0B]" onClick={() => onSave(i)} data-testid={`archive-save-button-${i}`}><BookmarkPlus className="h-4 w-4 mr-1.5" />Save as lore</Button>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
}

export default function AskPage() {
  const { universe, openEntity, base, canEdit } = useOutletContext();
  const navigate = useNavigate();
  const [log, setLog] = useState([]);
  const [saving, setSaving] = useState(null);
  const [question, setQuestion] = useState("");
  const endRef = useRef(null);
  const busy = log.some((x) => x.answer == null);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [log]);

  const ask = async (q = question) => {
    if (q.trim().length < 2 || busy) return;
    const history = log.filter((x) => x.answer).map(({ question: qq, answer }) => ({ question: qq, answer }));
    setQuestion("");
    setLog((l) => [...l, { question: q.trim(), answer: null }]);
    try {
      const { data } = await api.post(`/universes/${universe.id}/ai/ask`, { question: q.trim(), history });
      setLog((l) => l.map((x, i) => (i === l.length - 1 ? { ...x, ...data } : x)));
    } catch (e) {
      toast.error(errMsg(e));
      setLog((l) => l.slice(0, -1));
    }
  };
  const open = (c) => (c.type === "entry" ? openEntity(c.id) : navigate(`${base}/timelines?timeline=${c.timeline_id}&t=${c.timestamp_int}`));

  return (
    <div className="px-4 sm:px-8 lg:px-12 py-10 max-w-4xl">
      <PageHeader eyebrow="Archivist" title="Ask the Archive">
        {log.length > 0 && <Button variant="ghost" onClick={() => setLog([])} disabled={busy} data-testid="archive-clear-button"><RotateCcw className="h-4 w-4 mr-1.5" />New conversation</Button>}
      </PageHeader>
      {log.length === 0 && (
        <div className="mb-10" data-testid="archive-empty">
          <p className="text-[#A8A090] max-w-xl">Ask anything about your world. Answers come only from your Lore Bible and timelines, and every fact links to its source.</p>
          <div className="mt-5 flex flex-wrap gap-2">
            {EXAMPLES.map((ex, i) => (
              <button key={ex} onClick={() => ask(ex)} data-testid={`archive-example-${i}`}
                className="rounded-sm border border-dashed border-[#2D3442] px-3 py-1.5 text-sm text-[#A8A090] transition-colors hover:border-[#D97706]/60 hover:text-[#F3EBDD]">{ex}</button>
            ))}
          </div>
        </div>
      )}
      <div className="space-y-10 mb-10">{log.map((x, i) => <Exchange key={i} x={x} i={i} onOpen={open} canEdit={canEdit} onSave={setSaving} />)}</div>
      {saving != null && log[saving] && (
        <SaveAnswerDialog open onOpenChange={(o) => !o && setSaving(null)} uid={universe.id} exchange={log[saving]}
          onSaved={(id) => setLog((l) => l.map((x, j) => (j === saving ? { ...x, savedId: id } : x)))} />
      )}
      <div ref={endRef} />
      <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="sticky bottom-6 rounded-md border border-[#2D3442] bg-[#13171F]/90 backdrop-blur-md focus-within:border-[#D97706]/60 transition-colors">
        <Textarea rows={2} value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="How old is Kael in year 430?"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }}
          className="border-0 bg-transparent resize-none font-display text-xl focus-visible:ring-0 px-5 pt-4" data-testid="archive-question-input" />
        <div className="flex items-center justify-between px-4 pb-3">
          <span className="text-xs text-[#A8A090]">Enter to ask · Shift+Enter for a new line</span>
          <Button type="submit" size="sm" disabled={busy || question.trim().length < 2} data-testid="archive-ask-button"><Send className="h-4 w-4 mr-1.5" />Ask</Button>
        </div>
      </form>
    </div>
  );
}
